import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Close, CalendarToday } from "@mui/icons-material";
import InexButton from "../../../InexButton";
import { useAuthToken } from "../../../../hooks/auth";
import {
  useSetWhenBeginMutation,
} from "../../../../store/api/user";
import { useIsMobile } from "../../../../hooks/responsive";
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { fr } from 'date-fns/locale';


const WhenDialog = ({ open, onClose, whenBegin }) => {
  const isMobile = useIsMobile();

  const [currentWhenBegin, setCurrentWhenBegin] = React.useState(dayjs());

  React.useEffect(() => {
    const date1 = dayjs('1959-01-25');
    const date2 = dayjs(whenBegin)
    if(date2.diff(date1, 'year') >= 0 ) {
      setCurrentWhenBegin(date2)
    }
  }, [whenBegin])

  const diff = React.useMemo(() => {
    return currentWhenBegin !== dayjs(whenBegin);
  }, [whenBegin, currentWhenBegin]);

  const authToken = useAuthToken();

  const [setWhenBeginMutation] = useSetWhenBeginMutation();

  const handleValidate = async () => {
    // 1. On gère le "skip" manuellement
    if (!authToken) {
      console.warn("Action annulée : Aucun token d'authentification.");
      return;
    }

    try {
      // 2. On déclenche la mutation
      // .unwrap() permet de tomber dans le catch en cas d'erreur (403, 504, etc.)
      await setWhenBeginMutation({
        authToken,
        whenBegin: currentWhenBegin,
      }).unwrap();

      // 3. Succès : On ferme la modale
      onClose();

    } catch (err) {
      // 4. Gestion des erreurs Cloud Run
      console.error("Échec de la mutation :", err);
    }
  };

  return (
    <Dialog
      open={open}
      sx={{
        backgroundColor: (theme) => theme.palette.blackPro + "99",
      }}
      maxWidth="xl"
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.blackProLighter,
          color: "white",
          boxShadow: (theme) => theme.shadows[27],
          borderRadius: "15px",
          paddingLeft: isMobile ? 1 : 5,
          paddingRight: isMobile ? 1 : 5,
        },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, ml: 3, mr: 3, textAlign: "center" }}
        id="customized-dialog-title"
      >
        <Typography variant={"h3"}>
          {"Depuis quand exercez-vous ?"}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={fr}>
            <DemoContainer components={['DatePicker']}>
              <DatePicker
                placeholder={"YYYY"}
								format="YYYY"
                views={['year']}
                minDate={dayjs('1960-01-25')}
                maxDate={dayjs()}
                value={currentWhenBegin}
                onChange={(newValue) => {
                  setCurrentWhenBegin(newValue) 
                }}
                sx={{
                    backgroundColor: "#C7C4DE",
                    borderRadius: "30px",
                    width: "100%",
                    outline: "none",
                  }}
              />
            </DemoContainer>
          </LocalizationProvider>

        </Box>
      </DialogContent>
      <DialogActions>
        <InexButton
          text={"Annuler"}
          variant={"text"}
          textWithGradient={false}
          onClick={onClose}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: "white",
            color: "#202020",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: (theme) => theme.palette.primary.main,
              "& > p": {
                color: "white",
              },
            },
          }}
        />
        <InexButton
          text={"Valider"}
          variant={"text"}
          textWithGradient={false}
          onClick={handleValidate}
          disabled={!diff}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: (theme) =>
              !diff ? theme.palette.grey[400] : theme.palette.primary.main,
            color: "white",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: "white",
              "& > p": {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default WhenDialog;
